import { useEffect, useState } from "react";
import apiClient from "@/lib/apiClient";
import {
  Buildings, UsersThree, UserPlus, SignIn, Flask, CheckCircle,
  FileMagnifyingGlass, CreditCard, Repeat, CurrencyDollar, ShieldStar, ArrowSquareOut,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { HealthCards, EventStrip, SegmentGuide, ScoringDoc, useHealthSummary } from "@/components/owner/HealthSummary";
import { OpportunitiesTable, ContactCell } from "@/components/owner/OpportunitiesTable";
import { CohortTable } from "@/components/owner/CohortTable";

const tooltipStyle = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };

function Stat({ label, value, sub, icon: Icon, accent = "#2563EB" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`owner-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</span>
        <Icon size={18} color={accent} weight="bold" />
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-3 text-[#0F172A]">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

const Panel = ({ title, subtitle, children, testId, right }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="font-display font-bold text-lg text-[#0F172A]">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-blue-50 text-blue-700 border-blue-200",
  trial: "bg-slate-100 text-slate-600 border-slate-200",
  past_due: "bg-amber-50 text-amber-700 border-amber-200",
  canceled: "bg-red-50 text-red-600 border-red-200",
};

export default function OwnerDashboardPage() {
  const [ov, setOv] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [range, setRange] = useState(30); // 7, 30 or 90
  const [segment, setSegment] = useState("");
  const health = useHealthSummary(range);

  useEffect(() => {
    Promise.all([
      apiClient.get("/owner/overview"),
      apiClient.get("/owner/customers?limit=50"),
    ])
      .then(([o, c]) => { setOv(o.data); setCustomers(c.data.customers || []); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, []);

  if (denied) {
    return (
      <div className="max-w-lg mx-auto py-24 text-center" data-testid="owner-access-denied">
        <ShieldStar size={54} className="mx-auto text-slate-300" weight="fill" />
        <h1 className="font-display font-black text-2xl tracking-tighter text-[#0F172A] mt-5">Owner access only</h1>
        <p className="text-slate-500 mt-2">This area is restricted to the platform owner.</p>
      </div>
    );
  }
  if (loading || !ov) {
    return <div className="py-24 text-center text-slate-400" data-testid="owner-loading">Loading platform metrics…</div>;
  }

  const c = ov.cards;
  const sliced = (series) => (series || []).slice(-range);
  const trend = (key) => sliced(ov.trends[key]);

  const funnelSteps = [
    { label: "Registrations", value: ov.funnel.registrations },
    { label: "Demo Starts", value: ov.funnel.demo_starts },
    { label: "Demo Completions", value: ov.funnel.demo_completions },
    { label: "Purchases", value: ov.funnel.purchases },
  ];
  const funnelMax = Math.max(1, ...funnelSteps.map((s) => s.value));

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-blue-500 font-semibold">Platform Operations</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#0F172A] mt-2">Owner Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Aggregate health across every Proposal Forensics organization.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="owner-range-toggle">
            {[7, 30, 90].map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`owner-range-${r}`}
                className={`px-3 h-9 text-sm font-semibold transition-colors ${range === r ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {r}d
              </button>
            ))}
          </div>
          <a href={ov.ga4_dashboard_url} target="_blank" rel="noopener noreferrer" data-testid="owner-ga4-link"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
            GA4 Traffic <ArrowSquareOut size={15} weight="bold" />
          </a>
        </div>
      </div>

      {/* Aggregate cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        <Stat label="Companies" value={c.companies} sub={`${c.registrations_30d} new / 30d`} icon={Buildings} />
        <Stat label="Total Users" value={c.users} icon={UsersThree} />
        <Stat label="Registrations" value={c[`registrations_${range}d`]} sub={`${range}-day window`} icon={UserPlus} accent="#7C3AED" />
        <Stat label="Logins" value={c[`logins_${range}d`]} sub={`${range}-day window`} icon={SignIn} accent="#0891B2" />
        <Stat label="Demo Starts" value={c.demo_starts} icon={Flask} accent="#D97706" />
        <Stat label="Demo Completions" value={c.demo_completions} icon={CheckCircle} accent="#059669" />
        <Stat label="Proposals Analyzed" value={c.proposals_analyzed} icon={FileMagnifyingGlass} />
        <Stat label="Paid Conversions" value={c.paid_conversions} icon={CreditCard} accent="#7C3AED" />
        <Stat label="Active Subscriptions" value={c.active_paid_subscriptions} icon={Repeat} accent="#059669" />
        <Stat label="Revenue (30d)" value={money(c.revenue_30d)} sub={`${money(c.revenue_total)} all-time`} icon={CurrencyDollar} accent="#059669" />
      </div>

      {/* Trends */}
      <div className="grid md:grid-cols-2 gap-6">
        {[
          { key: "registrations", title: "New Registrations", color: "#7C3AED" },
          { key: "logins", title: "Successful Logins", color: "#0891B2" },
          { key: "demo_starts", title: "Demo Starts", color: "#D97706" },
          { key: "proposals", title: "Proposals Analyzed", color: "#2563EB" },
        ].map((t) => (
          <Panel key={t.key} title={t.title} subtitle={`Last ${range} days`} testId={`owner-trend-${t.key}`}>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={trend(t.key)} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id={`g-${t.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={t.color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={t.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} width={32} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={shortDate} />
                <Area type="monotone" dataKey="count" stroke={t.color} strokeWidth={2} fill={`url(#g-${t.key})`} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        ))}
      </div>

      {/* Funnel */}
      <Panel title="Conversion Funnel" subtitle="Registrations → demo starts → demo completions → purchases (visitor/acquisition data lives in GA4)" testId="owner-funnel">
        <div className="space-y-3">
          {funnelSteps.map((s, i) => {
            const prev = i === 0 ? null : funnelSteps[i - 1].value;
            const rate = prev ? Math.round((s.value / Math.max(1, prev)) * 100) : null;
            return (
              <div key={s.label} data-testid={`owner-funnel-${i}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold text-[#0F172A]">{s.label}</span>
                  <span className="text-slate-500">{s.value}{rate !== null && <span className="text-slate-400"> · {rate}% of prev</span>}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${Math.max(3, (s.value / funnelMax) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* Customer health & opportunities */}
      <div className="pt-4" data-testid="health-section">
        <div className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-emerald-600 font-semibold">Customer Success</div>
        <h2 className="font-display font-black text-2xl tracking-tighter text-[#0F172A] mt-1">Customer Health &amp; Opportunities</h2>
        <p className="text-xs text-slate-500 mt-1 max-w-3xl" data-testid="health-privacy-note">
          Privacy: scores use operational metadata only — timestamps, counts, plan and billing status. No proposal text, uploaded files, client names, findings, credentials, IP addresses or card data are read or exported. Contact emails are masked until revealed, and nothing here sends messages or changes plans.
        </p>
      </div>
      {health.error && <div className="text-sm text-red-600" data-testid="health-error">Could not load customer health.</div>}
      {health.data && (
        <>
          <HealthCards data={health.data} />
          <EventStrip data={health.data} />
          <Panel title="Lifecycle Segments" subtitle="Click a segment to filter the opportunities table. Numbers show precedence when a customer matches several." testId="owner-segments" right={null}>
            <SegmentGuide segments={health.data.segments} active={segment} onPick={setSegment} />
            <div className="mt-4"><ScoringDoc scoring={health.data.scoring} /></div>
          </Panel>
          <Panel title="Opportunities" subtitle="Lowest health first. Click a score to see its factors." testId="owner-opportunities">
            <OpportunitiesTable segments={health.data.segments} segment={segment} onSegment={setSegment} />
          </Panel>
          <Panel title="Cohorts & Retention" subtitle="By registration month" testId="owner-cohorts">
            <CohortTable cohorts={health.data.cohorts} minSample={health.data.min_sample} />
          </Panel>
        </>
      )}

      {/* Recent customers */}
      <Panel title="Recent Customer Accounts" subtitle="Newest organizations first" testId="owner-customers">
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left font-mono-plex text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-slate-200">
                <th className="py-2.5 px-2">Company</th>
                <th className="py-2.5 px-2">Plan</th>
                <th className="py-2.5 px-2">Status</th>
                <th className="py-2.5 px-2">Created</th>
                <th className="py-2.5 px-2">Last Login</th>
                <th className="py-2.5 px-2 text-right">Demo</th>
                <th className="py-2.5 px-2 text-right">Proposals</th>
                <th className="py-2.5 px-2 text-right">Spend</th>
                <th className="py-2.5 px-2">Support Email</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((r) => (
                <tr key={r.company_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`owner-customer-row-${r.company_id}`}>
                  <td className="py-2.5 px-2 font-semibold text-[#0F172A]">
                    {r.company_name}{r.is_demo && <span className="ml-2 font-mono-plex text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">demo</span>}
                  </td>
                  <td className="py-2.5 px-2 capitalize text-slate-600">{r.plan}</td>
                  <td className="py-2.5 px-2">
                    <span className={`font-mono-plex text-[9px] uppercase tracking-wider px-2 py-1 rounded border ${STATUS_STYLES[r.subscription_status] || STATUS_STYLES.trial}`}>{r.subscription_status}</span>
                  </td>
                  <td className="py-2.5 px-2 text-slate-500">{shortDate(r.created_at)}</td>
                  <td className="py-2.5 px-2 text-slate-500">{shortDate(r.last_login_at)}</td>
                  <td className="py-2.5 px-2 text-right text-slate-600">{r.demo_used}</td>
                  <td className="py-2.5 px-2 text-right text-slate-600">{r.proposal_count}</td>
                  <td className="py-2.5 px-2 text-right font-semibold text-[#0F172A]">{money(r.confirmed_spend)}</td>
                  <td className="py-2.5 px-2"><ContactCell row={{ company_id: r.company_id, support_email_masked: r.support_email ? `${r.support_email.split("@")[0].slice(0, 2)}***@${r.support_email.split("@")[1]}` : null }} /></td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-400">No customer accounts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
